import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronDown, Eye, MessageSquareText, Pencil, Trash2, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import type { Question, QuestionEdit, QuestionsApi } from '../api/questions'
import { QuestionFeedback } from './QuestionFeedback'
import { safeMarkup } from './safeMarkup'

/**
 * One Question, in a panel over the grid.
 *
 * **Read first, edit on demand.** The reason to open a Question is to look at it — did
 * the MathML come out right, did the diagram survive extraction — so the panel opens
 * showing the rendered thing, and turns into a form only when Edit is pressed. Fields
 * behind an edit toggle is Customer.io's pattern; showing the schema key under each label
 * is Supabase's, and it earns its place here because these Questions arrive as YAML files
 * and the key is what the person edits when they fix the file instead.
 *
 * Grouped and collapsible rather than one long form (Twenty): a Question is five
 * different kinds of thing — what it says, what the answers are, which is right, where it
 * is filed, where it came from.
 */
export function QuestionDrawer({
  api,
  id,
  onClose,
  onPreview,
}: {
  api: QuestionsApi
  id: number
  onClose: () => void
  onPreview: (id: number) => void
}) {
  const client = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [feedback, setFeedback] = useState(false)
  const [draft, setDraft] = useState<QuestionEdit | null>(null)

  const question = useQuery({
    queryKey: ['question', id],
    queryFn: () => api.read(id),
  })

  // Escape closes, which is the one keyboard affordance a panel over content must have.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const remove = useMutation({
    mutationFn: () => api.remove(id),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['questions'] })
      onClose()
    },
  })

  const save = useMutation({
    mutationFn: (edit: QuestionEdit) => api.save(id, edit),
    onSuccess: () => {
      setEditing(false)
      setDraft(null)
      void client.invalidateQueries({ queryKey: ['question', id] })
      void client.invalidateQueries({ queryKey: ['questions'] })
    },
  })

  const startEditing = (from: Question) => {
    setDraft(asEdit(from))
    setEditing(true)
  }

  return (
    <>
      {/* Click-away, and the dimming that says the grid is behind rather than gone. */}
      <div
        className="fixed inset-0 z-40 bg-ink-900/20"
        onClick={onClose}
        aria-hidden
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={`Question ${id}`}
        className="fixed top-0 right-0 z-50 flex h-full w-full max-w-5xl flex-col border-l border-ink-100 bg-card shadow-xl"
      >
        <header className="flex items-center gap-3 border-b border-ink-100 p-4">
          <h2 className="font-heading text-h3 font-bold text-heading">Question {id}</h2>
          <div className="ml-auto flex items-center gap-2">
            <button type="button" className={quiet} onClick={() => onPreview(id)}
              disabled={Boolean(question.data?.marking && question.data.marking.kind !== 'SingleChoice')}>
              <Eye aria-hidden size={16} /> Simulator
            </button>
            {!editing && (
              <button
                type="button"
                className={quiet}
                aria-pressed={feedback}
                onClick={() => setFeedback((was) => !was)}
              >
                <MessageSquareText aria-hidden size={16} /> View Feedback
              </button>
            )}
            {!editing && question.data && (
              <button
                type="button"
                className={quiet}
                disabled={Boolean(question.data.marking || question.data.stimulus)}
                onClick={() => {
                  setFeedback(false)
                  startEditing(question.data)
                }}
              >
                <Pencil aria-hidden size={16} /> Edit
              </button>
            )}
            {!editing && (
              <button
                type="button"
                className={`${quiet} border-red-700 text-red-700 hover:bg-red-50`}
                disabled={remove.isPending}
                onClick={() => {
                  // A Question is content somebody wrote; deleting it is not something to
                  // do on a stray click. The API refuses one that is placed in Practice or
                  // a Mock, so this only ever removes something nothing depends on.
                  if (window.confirm(`Delete Question ${id}? This cannot be undone.`)) remove.mutate()
                }}
              >
                <Trash2 aria-hidden size={16} /> Delete
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="rounded p-2 hover:bg-green-050"
            >
              <X aria-hidden size={18} />
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-4">
          {question.isPending && <p className="text-muted">Reading the Question...</p>}
          {question.isError && (
            <p role="alert" className="border-l-2 border-red-700 pl-3 text-red-700">
              {question.error.message}
            </p>
          )}

          {remove.isError && (
            <p role="alert" className="mb-3 border-l-2 border-red-700 pl-3 text-red-700">
              {remove.error.message}
            </p>
          )}

          {!editing && feedback && <QuestionFeedback api={api} id={id} />}
          {question.data && (question.data.marking || question.data.stimulus) && (
            <p className="mb-3 text-muted">Re-upload the complete question file to edit its shared passage or answer rule.</p>
          )}
          {question.data && !editing && !feedback && <Reading question={question.data} />}
          {question.data && editing && draft && (
            <Editing draft={draft} onChange={setDraft} />
          )}
        </div>

        {editing && draft && (
          <footer className="border-t border-ink-100 p-4">
            {save.isError && (
              <p role="alert" className="mb-3 text-red-700">
                {save.error.message}
              </p>
            )}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                className={quiet}
                onClick={() => {
                  setEditing(false)
                  setDraft(null)
                }}
              >
                Discard
              </button>
              <button
                type="button"
                className={loud}
                disabled={save.isPending}
                onClick={() => save.mutate(draft)}
              >
                {save.isPending ? 'Saving...' : 'Save changes'}
              </button>
            </div>
          </footer>
        )}
      </aside>
    </>
  )
}

/**
 * The Question as it will look, which is the whole reason for opening it.
 *
 * The stem, the Options and the Solution are the authored HTML, rendered — not escaped
 * into a code block. That is a deliberate choice and it is only safe because the intake
 * refuses `script`, `iframe`, `img`, event handlers and off-page references before a
 * Question is ever stored (QuestionUploadValidator, the Rendering rules). Nothing reaches
 * here that did not pass them.
 *
 * MathML renders natively in every current browser, so the maths is readable without
 * MathJax on this screen; the Simulator is where the typeset-for-real check belongs, and
 * that is one button away.
 */
function Reading({ question }: { question: Question }) {
  return (
    <div className="grid gap-4 lg:grid-cols-2 lg:items-start">
      <div className="space-y-4">
      {question.stimulus?.html && <Group label="Shared passage"><Rendered html={question.stimulus.html} /></Group>}
      <Group label="Question">
        <Rendered html={question.stem} />
      </Group>

      {(!question.marking || question.marking.kind === 'SingleChoice') && <Group label={`Options (${question.options.length})`}>
        <ol className="space-y-2">
          {question.options.map((option) => {
            const correct = option.letter === question.correct
            return (
              <li
                key={option.letter}
                className={`rounded border p-3 ${correct ? 'border-green-800 bg-green-050' : 'border-ink-100'}`}
              >
                <div className="flex gap-3">
                  <span className="font-bold">{option.letter}</span>
                  <div className="flex-1">
                    <Rendered html={option.html} />
                  </div>
                  {/* Word as well as colour, for the same reason as the grid column. */}
                  {correct && <span className="font-bold text-green-800">Correct</span>}
                </div>
              </li>
            )
          })}
        </ol>
        {question.correct === null && (
          <p className="mt-2 text-muted">
            No Correct Option — the source did not settle it, so a Checker still has to.
          </p>
        )}
      </Group>}

      {question.marking && <Group label="Answer rule">
        <p>{question.marking.kind === 'Unscored' ? 'Unscored writing' : `${question.marking.maximumMarks} maximum marks`}</p>
        {question.marking.partialAt != null && <p>{question.marking.partialMarks} marks for {question.marking.partialAt} correct parts</p>}
        {(question.marking.parts ?? []).map((part) => <div key={part.code} className="mt-3 space-y-2">
          <Rendered html={part.prompt} />
          <ul>{part.options.map((option) => <li key={option.position}>
            <Rendered html={option.html} />
            {option.position === part.correctPosition && <span>Correct</span>}
            {option.credit != null && <span>{option.credit} marks</span>}
          </li>)}</ul>
        </div>)}
      </Group>}

      </div>

      <div className="space-y-4">
      {question.solution && (
        <Group label="Solution">
          <Rendered html={question.solution} />
        </Group>
      )}
      {question.officialSolution && (
        <Group label="Official Solution">
          <Rendered html={question.officialSolution} />
        </Group>
      )}
      {question.hint && (
        <Group label="Hint">
          <Rendered html={question.hint} />
        </Group>
      )}
      {question.tip && (
        <Group label="Tip">
          <p>{question.tip}</p>
        </Group>
      )}

      <Group label="Filing">
        <Facts
          facts={[
            ['Kind', question.kind, 'type'],
            ['Exam', question.exam, 'exam_code'],
            ['Topic Code', question.topicCode, 'topic_code'],
            ['Topic', question.topic, 'topic'],
            ['Subtopics', question.subtopics?.join(', '), 'subtopics'],
            ['Difficulty', question.difficulty, 'difficulty'],
          ]}
        />
      </Group>

      <Group label="Placement">
        <Facts
          facts={[
            ['Placement', question.placement, null],
            ['Order', question.examOrder.toString(), null],
            ['Paper', question.componentCode, null],
            ['Complete', question.isComplete ? 'Yes' : 'No', null],
            ['Authored on', question.authoredOn ? `${new Date(question.authoredOn).toLocaleTimeString('en-GB', { timeZone: 'Europe/London', hour12: false })} ${new Date(question.authoredOn).toLocaleDateString('en-GB', { timeZone: 'Europe/London', day: '2-digit', month: '2-digit', year: '2-digit' })}` : null, null],
            ['Added', new Date(question.createdAt).toLocaleString('en-GB'), null],
          ]}
        />
      </Group>
      </div>
    </div>
  )
}

/** The same fields, as a form. Every input names the YAML key it corresponds to. */
function Editing({
  draft,
  onChange,
}: {
  draft: QuestionEdit
  onChange: (next: QuestionEdit) => void
}) {
  const set = <K extends keyof QuestionEdit>(key: K, value: QuestionEdit[K]) =>
    onChange({ ...draft, [key]: value })

  return (
    <div className="grid gap-4 lg:grid-cols-2 lg:items-start">
      <div className="space-y-4">
      <Group label="Question">
        <Field label="Stem" schemaKey="stem" hint="HTML, with MathML for maths and inline SVG for diagrams.">
          <textarea
            className={`${input} h-40 font-mono`}
            value={draft.stem ?? ''}
            onChange={(event) => set('stem', event.target.value)}
          />
        </Field>
      </Group>

      <Group label="Options">
        {Object.entries(draft.options ?? {}).map(([letter, html]) => (
          <Field key={letter} label={`Option ${letter}`} schemaKey={`options.${letter}`}>
            <textarea
              className={`${input} h-20 font-mono`}
              value={html}
              onChange={(event) =>
                set('options', { ...(draft.options ?? {}), [letter]: event.target.value })
              }
            />
          </Field>
        ))}
        <Field label="Correct Option" schemaKey="correct" hint="The letter. Leave empty where the source does not settle it.">
          <input
            className={input}
            value={draft.correct ?? ''}
            onChange={(event) => set('correct', event.target.value.toUpperCase())}
          />
        </Field>
      </Group>

      </div>

      <div className="space-y-4">
      <Group label="Answer">
        <Field label="Solution" schemaKey="solution" hint="HTML.">
          <textarea
            className={`${input} h-32 font-mono`}
            value={draft.solution ?? ''}
            onChange={(event) => set('solution', event.target.value)}
          />
        </Field>
        <Field label="Official Solution" schemaKey="official_solution" hint="HTML. The exam board's worked solution, verbatim. Official only.">
          <textarea
            className={`${input} h-32 font-mono`}
            value={draft.officialSolution ?? ''}
            onChange={(event) => set('officialSolution', event.target.value)}
          />
        </Field>
        <Field label="Hint" schemaKey="hint" hint="One step towards the Solution, shown in Practice.">
          <textarea
            className={`${input} h-20 font-mono`}
            value={draft.hint ?? ''}
            onChange={(event) => set('hint', event.target.value)}
          />
        </Field>
        <Field label="Tip" schemaKey="tip" hint="Plain prose. The shortcut a Student short of time takes instead of the Solution.">
          <textarea
            className={`${input} h-20`}
            value={draft.tip ?? ''}
            onChange={(event) => set('tip', event.target.value)}
          />
        </Field>
      </Group>

      <Group label="Filing">
        <Field label="Topic Code" schemaKey="topic_code">
          <input
            className={input}
            value={draft.topicCode ?? ''}
            onChange={(event) => set('topicCode', event.target.value)}
          />
        </Field>
        <Field label="Topic" schemaKey="topic">
          <input
            className={input}
            value={draft.topic ?? ''}
            onChange={(event) => set('topic', event.target.value)}
          />
        </Field>
        <Field label="Difficulty" schemaKey="difficulty">
          <select
            className={input}
            value={draft.difficulty ?? ''}
            onChange={(event) =>
              set('difficulty', event.target.value === '' ? null : event.target.value as NonNullable<QuestionEdit['difficulty']>)
            }
          >
            <option value="">Not set</option>
            {difficulties.map((difficulty) => (
              <option key={difficulty} value={difficulty}>
                {difficulty}
              </option>
            ))}
          </select>
        </Field>
      </Group>
      </div>
    </div>
  )
}

/** Authored markup, rendered. See Reading's note on why this is safe. */
function Rendered({ html }: { html: string }) {
  return <div className="prose-question" dangerouslySetInnerHTML={{ __html: safeMarkup(html) }} />
}

/**
 * A section of the panel, open unless it is asked to be shut.
 *
 * Open by default because the panel exists to be read: a Solution or a Tip behind a
 * closed disclosure is a Solution nobody checks, and the right-hand column was every one
 * of them. Collapsing stays available for a stem long enough to want the room back.
 */
function Group({
  label,
  open = true,
  children,
}: {
  label: string
  open?: boolean
  children: React.ReactNode
}) {
  return (
    <details open={open} className="rounded-lg border border-ink-100">
      <summary className="flex cursor-pointer items-center gap-2 p-3 font-bold text-heading">
        <ChevronDown aria-hidden size={16} className="transition-transform" />
        {label}
      </summary>
      <div className="border-t border-ink-100 p-3">{children}</div>
    </details>
  )
}

/** A label, the YAML key it maps to, and the value. Empty reads as an em dash, not blank. */
function Facts({ facts }: { facts: [string, string | null | undefined, string | null][] }) {
  return (
    <dl className="grid grid-cols-[minmax(0,10rem)_minmax(0,1fr)] gap-x-4 gap-y-2">
      {facts.map(([label, value, schemaKey]) => (
        <div key={label} className="contents">
          <dt className="text-muted">
            {label}
            {schemaKey && <code className="ml-2 text-xs">{schemaKey}</code>}
          </dt>
          <dd className="break-words">{value === null || value === undefined || value === '' ? '—' : value}</dd>
        </div>
      ))}
    </dl>
  )
}

function Field({
  label,
  schemaKey,
  hint,
  children,
}: {
  label: string
  schemaKey: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <label className="mt-3 block first:mt-0">
      <span className="font-bold text-heading">{label}</span>
      <code className="ml-2 text-xs text-muted">{schemaKey}</code>
      {children}
      {hint && <span className="mt-1 block text-muted">{hint}</span>}
    </label>
  )
}

/**
 * A Question as the editing form's body. The same shape a file carries, so a Question
 * edited here and a Question re-uploaded go through one set of rules on the API.
 */
function asEdit(question: Question): QuestionEdit {
  return {
    type: question.kind,
    examCode: question.exam,
    topicCode: question.topicCode,
    topic: question.topic,
    subtopics: question.subtopics,
    difficulty: question.difficulty,
    stem: question.stem,
    options: Object.fromEntries(question.options.map((option) => [option.letter, option.html])),
    correct: question.correct,
    solution: question.solution,
    officialSolution: question.officialSolution,
    hint: question.hint,
    tip: question.tip,
  }
}

const input = 'mt-1 w-full rounded border border-ink-100 px-3 py-2'

const difficulties = ['Easy', 'Medium', 'Hard'] as const

const quiet =
  'inline-flex items-center gap-2 rounded-md border border-green-800 px-3 py-1.5 font-bold text-green-800 hover:bg-green-050 disabled:opacity-55'

const loud =
  'inline-flex items-center gap-2 rounded-md bg-green-800 px-4 py-2 font-bold text-white hover:bg-green-900 disabled:opacity-55'
